import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { t } from '../i18n';

const colors = {
  bg: '#F7F5F0',
  surface: '#fff',
  border: '#E3E1D9',
  text: '#1F1E1B',
  textMuted: '#5F5E5A',
  primary: '#0F6E56',
};

const API_BASE = '/api/services';

function getAuthToken() {
  return localStorage.getItem('token') || '';
}

export default function ServiceProviderProfile({ user }) {
  const { id } = useParams();
  const [provider, setProvider] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [err, setErr] = useState('');
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/providers/${id}/`);
      const data = await res.json().catch(() => ({}));
      if (res.ok) { setProvider(data); setErr(''); }
      else { setProvider(null); setErr(data.error || `${t('could_not_load_provider')} (${res.status})`); }
    } catch (e) {
      setProvider(null);
      setErr(t('could_not_reach_server'));
    }
  }, [id]);

  const loadReviews = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/providers/${id}/reviews/`);
      const data = await res.json().catch(() => ({}));
      if (res.ok) setReviews(Array.isArray(data.reviews) ? data.reviews : []);
    } catch (e) {}
  }, [id]);

  useEffect(() => { load(); loadReviews(); }, [load, loadReviews]);

  const submitReview = async () => {
    if (!user) { window.location.href = '/'; return; }
    setPosting(true);
    try {
      const res = await fetch(`${API_BASE}/providers/${id}/reviews/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Token ${getAuthToken()}` },
        body: JSON.stringify({ rating, comment }),
      });
      if (res.ok) { setComment(''); loadReviews(); }
    } catch (e) {}
    setPosting(false);
  };

  if (err) {
    return <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{err}</p>;
  }
  if (!provider) {
    return <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('loading')}</p>;
  }

  const waLink = provider.whatsapp ? `https://wa.me/${provider.whatsapp.replace(/[^0-9]/g, '')}` : null;

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: '1.5rem', background: colors.bg, minHeight: '100vh', fontFamily: 'sans-serif' }}>
      <a href="/services" style={{ fontSize: 12, color: colors.textMuted, textDecoration: 'none' }}>&larr; {t('services')}</a>

      <div style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 14, padding: '1.2rem', marginTop: 10 }}>
        <p style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
          {provider.category_icon} {provider.business_name}
          {provider.is_verified ? <span style={{ color: colors.primary, marginLeft: 6, fontSize: 12 }}>✔ {t('verified')}</span> : null}
        </p>
        <p style={{ margin: '4px 0', fontSize: 13, color: colors.textMuted }}>
          {provider.category_display} · {provider.city}{provider.area ? `, ${provider.area}` : ''}
        </p>
        <p style={{ margin: '4px 0', fontSize: 13, color: colors.textMuted }}>
          {t('years_experience')}: {provider.years_experience} · {t('rating')}: {provider.avg_rating} ({provider.reviews_count})
        </p>
        {provider.description ? (
          <p style={{ margin: '10px 0', fontSize: 13, color: colors.text }}>{provider.description}</p>
        ) : null}
        {provider.hourly_rate_pi ? (
          <p style={{ margin: '4px 0', fontSize: 14, fontWeight: 700, color: colors.primary }}>π{provider.hourly_rate_pi}/{t('hr')}</p>
        ) : null}

        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          {provider.phone ? (
            <a href={`tel:${provider.phone}`} style={{ flex: 1, textAlign: 'center', padding: '10px 0', borderRadius: 10, background: colors.bg, border: `0.5px solid ${colors.border}`, fontSize: 13, textDecoration: 'none', color: colors.text }}>
              📞 {t('call')}
            </a>
          ) : null}
          {waLink ? (
            <a href={waLink} target="_blank" rel="noreferrer" style={{ flex: 1, textAlign: 'center', padding: '10px 0', borderRadius: 10, background: colors.bg, border: `0.5px solid ${colors.border}`, fontSize: 13, textDecoration: 'none', color: colors.text }}>
              💬 {t('whatsapp')}
            </a>
          ) : null}
          {provider.username ? (
            <a href={`/chat/${provider.username}`} style={{ flex: 1, textAlign: 'center', padding: '10px 0', borderRadius: 10, background: colors.primary, color: '#fff', fontSize: 13, textDecoration: 'none' }}>
              💌 {t('chat')}
            </a>
          ) : null}
        </div>
      </div>

      <div style={{ marginTop: '1.2rem' }}>
        <p style={{ fontSize: 14, fontWeight: 700, margin: '0 0 8px' }}>{t('reviews')}</p>
        {reviews.length === 0 ? (
          <p style={{ fontSize: 12, color: colors.textMuted }}>{t('no_reviews_yet')}</p>
        ) : (
          reviews.map((r) => (
            <div key={r.id} style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 10, padding: '0.7rem', marginBottom: 6 }}>
              <p style={{ margin: 0, fontSize: 12, fontWeight: 600 }}>{r.client_username} — {r.rating}★</p>
              {r.comment ? <p style={{ margin: '2px 0 0', fontSize: 12, color: colors.textMuted }}>{r.comment}</p> : null}
            </div>
          ))
        )}

        <div style={{ marginTop: 10 }}>
          <select value={rating} onChange={(e) => setRating(Number(e.target.value))} style={{ marginBottom: 6, padding: 6, borderRadius: 8, border: `0.5px solid ${colors.border}` }}>
            {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} ★</option>)}
          </select>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('write_a_review')}
            style={{ width: '100%', minHeight: 60, padding: 8, borderRadius: 8, border: `0.5px solid ${colors.border}`, fontSize: 13, boxSizing: 'border-box' }}
          />
          <button
            onClick={submitReview}
            disabled={posting}
            style={{ marginTop: 6, padding: '8px 16px', borderRadius: 8, background: colors.primary, color: '#fff', border: 'none', fontSize: 13, cursor: 'pointer' }}
          >
            {posting ? t('posting') : t('submit_review')}
          </button>
        </div>
      </div>
    </div>
  );
}
